import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Settings,
  UserPlus,
  Crown,
  Shield,
  Eye,
  Mail,
  Trash2,
  Edit,
  Check,
  X,
  Copy,
  RefreshCw,
  Share2,
  Building2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [sharedResources, setSharedResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingShared, setLoadingShared] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [teamForm, setTeamForm] = useState({ name: "", description: "" });
  const [inviteForm, setInviteForm] = useState({ email: "", role: "member" });

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchMembers(selectedTeam.id);
      fetchSharedResources();
    }
  }, [selectedTeam]);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const res = await api.get("/teams");
      setTeams(res.data.teams || []);
      if (res.data.teams?.length > 0 && !selectedTeam) {
        setSelectedTeam(res.data.teams[0]);
      }
    } catch (e) {
      console.error("Error fetching teams:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async (teamId) => {
    try {
      const res = await api.get(`/teams/${teamId}/members`);
      setMembers(res.data.members || []);
    } catch (e) {
      console.error("Error fetching members:", e);
    }
  };

  const fetchSharedResources = async () => {
    if (!selectedTeam) return;
    setLoadingShared(true);
    try {
      const res = await api.get(`/teams/${selectedTeam.id}/shared`);
      setSharedResources(res.data.shared_resources || []);
    } catch (e) {
      console.error("Error fetching shared resources:", e);
    } finally {
      setLoadingShared(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!teamForm.name.trim()) {
      toast.error("Team name is required");
      return;
    }

    setSaving(true);
    try {
      const res = await api.post("/teams", teamForm);
      toast.success("Team created!");
      setShowCreateTeam(false);
      setTeamForm({ name: "", description: "" });
      fetchTeams();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create team");
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email.trim()) {
      toast.error("Email is required");
      return;
    }

    setSaving(true);
    try {
      const res = await api.post(`/teams/${selectedTeam.id}/invite`, inviteForm);
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setShowInvite(false);
      setInviteForm({ email: "", role: "member" });
      fetchMembers(selectedTeam.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invite");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!confirm("Remove this member from the team?")) return;
    
    try {
      await api.delete(`/teams/${selectedTeam.id}/members/${memberId}`);
      toast.success("Member removed");
      fetchMembers(selectedTeam.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove member");
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await api.put(`/teams/${selectedTeam.id}/members/${memberId}/role`, { role: newRole });
      toast.success("Role updated");
      fetchMembers(selectedTeam.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update role");
    }
  };

  const handleLeaveTeam = async () => {
    if (!confirm("Leave this team?")) return;
    
    try {
      // Find current user's membership
      const myMembership = members.find(m => m.role !== "owner");
      if (myMembership) {
        await api.delete(`/teams/${selectedTeam.id}/members/${myMembership.id}`);
        toast.success("Left the team");
        setSelectedTeam(null);
        fetchTeams();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to leave team");
    }
  };

  const handleDeleteTeam = async () => {
    if (!confirm(`Are you sure you want to delete "${selectedTeam.name}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/teams/${selectedTeam.id}`);
      toast.success("Team deleted");
      setSelectedTeam(null);
      setMembers([]);
      setSharedResources([]);
      fetchTeams();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete team");
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin": return <Crown className="w-4 h-4 text-indigo-400" />;
      case "member": return <Shield className="w-4 h-4 text-indigo-400" />;
      case "viewer": return <Eye className="w-4 h-4 text-indigo-400" />;
      default: return null;
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
      member: "bg-blue-500/20 text-indigo-400 border-slate-400/30",
      viewer: "bg-slate-500/20 text-indigo-400 border-slate-400/30",
      pending: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30"
    };
    return styles[role] || styles.member;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="teams">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-indigo-500 flex items-center gap-2">
            <Users className="w-7 h-7 text-indigo-400" />
            Teams
          </h1>
          <p className="text-indigo-400">Collaborate with your team members</p>
        </div>
        <button
          onClick={() => setShowCreateTeam(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
          data-testid="create-team-btn"
        >
          <Plus className="w-4 h-4" />
          Create Team
        </button>
      </div>

      {teams.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-400 mb-2">No teams yet</h3>
            <p className="text-slate-400 mb-4">
              Create a team to start collaborating with others
            </p>
            <button
              onClick={() => setShowCreateTeam(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg"
            >
              <Plus className="w-4 h-4" />
              Create Your First Team
            </button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Team List */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-indigo-400">Your Teams</h3>
            {teams.map((team) => (
              <button
                key={team.id}
                onClick={() => setSelectedTeam(team)}
                className={`w-full text-left p-4 rounded-xl transition-all ${
                  selectedTeam?.id === team.id
                    ? "bg-indigo-500/20 border-2 border-indigo-500/50"
                    : "bg-slate-800/50 border border-slate-700/50 hover:border-slate-600"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-indigo-500 truncate">{team.name}</h4>
                    <p className="text-xs text-indigo-400">{team.member_count || 1} members</p>
                  </div>
                  <Badge className={getRoleBadge(team.role)}>
                    {team.role}
                  </Badge>
                </div>
              </button>
            ))}
          </div>

          {/* Team Details */}
          {selectedTeam && (
            <div className="lg:col-span-2 space-y-4">
              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-indigo-500">{selectedTeam.name}</CardTitle>
                      <CardDescription className="text-indigo-400">
                        {selectedTeam.description || "No description"}
                      </CardDescription>
                    </div>
                    {selectedTeam.role === "admin" && (
                      <button
                        onClick={() => setShowInvite(true)}
                        className="flex items-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-indigo-500 rounded-lg text-sm"
                        data-testid="invite-member-btn"
                      >
                        <UserPlus className="w-4 h-4" />
                        Invite
                      </button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <h4 className="text-sm font-medium text-slate-400 mb-3">Team Members</h4>
                  <div className="space-y-2">
                    {members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="w-9 h-9">
                            <AvatarFallback className="bg-indigo-500/20 text-indigo-400 text-sm">
                              {(member.name || member.email || "?")[0].toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium text-indigo-500">
                              {member.name || member.email}
                            </p>
                            <p className="text-xs text-slate-400">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {member.status === "pending" ? (
                            <Badge className={getRoleBadge("pending")}>Pending</Badge>
                          ) : (
                            <>
                              <div className="flex items-center gap-1">
                                {getRoleIcon(member.role)}
                                <span className="text-sm text-indigo-400 capitalize">
                                  {member.role}
                                </span>
                              </div>
                              {selectedTeam.role === "admin" && member.role !== "admin" && (
                                <button
                                  onClick={() => handleRemoveMember(member.id)}
                                  className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Shared Resources */}
              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-indigo-500 text-lg flex items-center gap-2">
                      <Share2 className="w-5 h-5 text-green-400" />
                      Shared Resources
                    </CardTitle>
                    <button
                      onClick={fetchSharedResources}
                      className="text-indigo-400 hover:text-indigo-500 p-1"
                    >
                      <RefreshCw className={`w-4 h-4 ${loadingShared ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </CardHeader>
                <CardContent>
                  {sharedResources.length > 0 ? (
                    <div className="space-y-2">
                      {sharedResources.map((resource) => (
                        <div
                          key={resource.id}
                          className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              resource.resource_type === 'client' ? 'bg-blue-500/20' :
                              resource.resource_type === 'project' ? 'bg-indigo-500/20' :
                              'bg-indigo-500/20'
                            }`}>
                              {resource.resource_type === 'client' && <Users className="w-4 h-4 text-indigo-400" />}
                              {resource.resource_type === 'project' && <Building2 className="w-4 h-4 text-indigo-400" />}
                              {resource.resource_type === 'template' && <Mail className="w-4 h-4 text-indigo-400" />}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-indigo-500 capitalize">
                                {resource.resource_type}
                              </p>
                              <p className="text-xs text-slate-400">
                                Shared {new Date(resource.shared_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Badge className={resource.permissions === 'edit' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-indigo-400'}>
                            {resource.permissions}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <Share2 className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                      <p className="text-indigo-400 text-sm">
                        No resources shared with this team yet
                      </p>
                      <p className="text-slate-400 text-xs mt-1">
                        Share clients, projects, or templates from their respective pages
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Team Actions */}
              {selectedTeam.role === "admin" && (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardHeader>
                    <CardTitle className="text-indigo-500 text-lg flex items-center gap-2">
                      <Settings className="w-5 h-5 text-indigo-400" />
                      Team Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-indigo-500">Team Name</p>
                        <p className="text-xs text-slate-400">{selectedTeam.name}</p>
                      </div>
                      <button className="text-indigo-400 hover:text-indigo-400 text-sm">
                        Edit
                      </button>
                    </div>
                    {selectedTeam.owner_id === members.find(m => m.role === "admin")?.user_id && (
                      <button
                        onClick={handleDeleteTeam}
                        className="w-full flex items-center justify-center gap-2 p-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                        data-testid="delete-team-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Team
                      </button>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create Team Modal */}
      <Dialog open={showCreateTeam} onOpenChange={setShowCreateTeam}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-indigo-500">Create Team</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-400">Team Name</Label>
              <Input
                value={teamForm.name}
                onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                placeholder="e.g., My Agency"
                className="bg-slate-800 border-slate-700 text-indigo-500 mt-1"
                data-testid="team-name-input"
              />
            </div>
            <div>
              <Label className="text-slate-400">Description (optional)</Label>
              <Textarea
                value={teamForm.description}
                onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                placeholder="What's this team for?"
                rows={3}
                className="bg-slate-800 border-slate-700 text-indigo-500 mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <button
              onClick={() => setShowCreateTeam(false)}
              className="px-4 py-2 text-indigo-400 hover:text-indigo-500"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateTeam}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg"
              data-testid="save-team-btn"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create Team
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Member Modal */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-indigo-500">Invite Team Member</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-400">Email Address</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="colleague@example.com"
                className="bg-slate-800 border-slate-700 text-indigo-500 mt-1"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label className="text-slate-400">Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="member" className="text-indigo-500">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-indigo-400" />
                      Member - Can view and edit
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer" className="text-indigo-500">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-indigo-400" />
                      Viewer - View only
                    </div>
                  </SelectItem>
                  <SelectItem value="admin" className="text-indigo-500">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-indigo-400" />
                      Admin - Full access
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <button
              onClick={() => setShowInvite(false)}
              className="px-4 py-2 text-indigo-400 hover:text-indigo-500"
            >
              Cancel
            </button>
            <button
              onClick={handleInvite}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-indigo-500 rounded-lg"
              data-testid="send-invite-btn"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Send Invite
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
